import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { fetchDiocesan } from '@/services/api';
import { motion } from 'framer-motion';
import { 
  GraduationCap, 
  Cross, 
  Target, 
  Award, 
  Calendar, 
  User,
  Quote
} from 'lucide-react';

const BASE_URL = `https://api.ibadansouthanglicandiocese.org.ng/`;

interface DiocesanInfo {
  photo: string;
  name: string;
}

const Bishop = () => {
  const [loading, setLoading] = useState(true);
  const [diocesan, setDiocesan] = useState<DiocesanInfo | null>(null);

  useEffect(() => {
    const loadDiocesan = async () => {
      try {
        const res = await fetchDiocesan();
        setDiocesan(res.data[0]);
      } catch (err) {
        console.error('Failed to fetch diocesan info:', err);
      } finally {
        setLoading(false);
      }
    };
    loadDiocesan();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fcfaf7] flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="h-12 w-12 border-4 border-amber-600/20 border-t-amber-600 rounded-full animate-spin mb-4" />
          <p className="text-slate-500 font-serif italic">Ascertaining Diocesan Records...</p>
        </div>
      </div>
    );
  }

  const fullPhotoUrl = diocesan?.photo?.startsWith('http') 
    ? diocesan.photo 
    : `${BASE_URL}${diocesan?.photo}`;

  return (
    <div className="min-h-screen bg-[#fcfaf7] pb-24">
      {/* Hero Section */}
      <section className="relative h-[40vh] bg-slate-900 overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />
        <div className="relative z-10 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-3 mb-4"
          >
            <div className="h-px w-8 bg-amber-500" />
            <span className="text-amber-500 uppercase tracking-[0.4em] text-xs font-bold">The Episcopacy</span>
            <div className="h-px w-8 bg-amber-500" />
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-serif font-bold text-white px-4">
            Our Lord Bishop
          </h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 -mt-20 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Left Column: Photo & Brief */}
          <div className="lg:col-span-4">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="sticky top-28"
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-amber-600 rounded-[2.5rem] translate-x-3 translate-y-3" />
                <div className="relative aspect-[3/4] rounded-[2.5rem] overflow-hidden bg-slate-200 shadow-2xl">
                  <img 
                    src={fullPhotoUrl} 
                    alt={diocesan?.name} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>

              <div className="mt-8 bg-white rounded-3xl p-8 shadow-sm border border-slate-100 text-center">
                <h3 className="text-2xl font-serif font-bold text-slate-900">{diocesan?.name}</h3>
                <p className="text-amber-600 font-bold text-sm uppercase tracking-widest mt-2">
                  Bishop of Ibadan South
                </p>
                <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t border-slate-50">
                   <div className="text-center">
                      <p className="text-[10px] uppercase text-slate-400 font-bold mb-1">Ordained</p>
                      <p className="text-sm font-bold text-slate-700">1996</p>
                   </div>
                   <div className="w-px h-8 bg-slate-100" />
                   <div className="text-center">
                      <p className="text-[10px] uppercase text-slate-400 font-bold mb-1">Consecrated</p>
                      <p className="text-sm font-bold text-slate-700">2008</p>
                   </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Column: Content */}
          <div className="lg:col-span-8 space-y-10">
            
            {/* Biography */}
            <Card className="rounded-[2.5rem] border-none shadow-sm bg-white overflow-hidden">
              <div className="p-8 md:p-12">
                <div className="flex items-center gap-3 mb-8">
                  <User className="text-amber-600 w-5 h-5" />
                  <h2 className="text-2xl font-serif font-bold text-slate-900">Episcopal Biography</h2>
                </div>
                
                <div className="prose prose-slate max-w-none space-y-6 font-serif text-lg leading-relaxed text-slate-700">
                  <p>
                    The Right Reverend <span className="font-bold text-slate-900">AkinTunde Adedayo Popoola</span> brings a unique synthesis of spiritual depth and technical expertise to the Diocese of Ibadan South. Born with a mission for transformation, his leadership is characterized by a "Digital Evangelism" philosophy.
                  </p>
                  
                  <div className="bg-slate-50 border-l-4 border-amber-500 p-6 my-8 italic relative">
                    <Quote className="absolute top-2 right-4 w-12 h-12 text-slate-200 -z-0" />
                    <p className="relative z-10">
                      "A passionate evangelist, he has been actively involved in church planting, growth, and strategic expansion wherever he served."
                    </p>
                  </div>

                  <p>
                    With a background in Computer Engineering from <span className="italic">Obafemi Awolowo University</span>, Bishop Popoola has pioneered the digitalization of communication within the Church of Nigeria, serving as the Director of Communications before his translation to Ibadan South in 2018.
                  </p>
                </div>
              </div>
            </Card>

            {/* Qualifications - Timeline Style */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
               <Card className="rounded-[2.5rem] border-none shadow-sm bg-white p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <GraduationCap className="text-amber-600 w-5 h-5" />
                    <h2 className="text-xl font-serif font-bold">Academic Journey</h2>
                  </div>
                  <ul className="space-y-6">
                    {[
                      { year: '1989', degree: 'B.Sc. Computer Engineering', school: 'OAU, Ile-Ife' },
                      { year: '1998', degree: 'Diploma in Theology', school: 'Immanuel College' },
                      { year: '2001', degree: 'Master in Managerial Psychology', school: 'University of Ibadan' },
                      { year: '2009', degree: 'Master of Divinity', school: 'TCNN, Bukuru' },
                    ].map((item, idx) => (
                      <li key={idx} className="relative pl-6 border-l border-slate-100">
                        <div className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-amber-500" />
                        <span className="text-[10px] font-bold text-amber-600 uppercase tracking-tighter">{item.year}</span>
                        <p className="text-sm font-bold text-slate-800 leading-tight">{item.degree}</p>
                        <p className="text-xs text-slate-500 font-serif">{item.school}</p>
                      </li>
                    ))}
                  </ul>
               </Card>

               {/* Ministry Focus */}
               <Card className="rounded-[2.5rem] border-none shadow-sm bg-slate-900 text-white p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Target className="text-amber-500 w-5 h-5" />
                    <h2 className="text-xl font-serif font-bold">Ministry Pillars</h2>
                  </div>
                  <div className="space-y-4">
                    {[
                      'Evangelism & Church Planting',
                      'Digital & Communication Ministry',
                      'Youth & Campus Outreach',
                      'Clergy Strategic Training',
                      'Leadership Development'
                    ].map((pill, i) => (
                      <div key={i} className="flex items-center gap-3 bg-white/5 p-3 rounded-xl border border-white/10">
                        <Cross className="w-3 h-3 text-amber-500" />
                        <span className="text-sm font-medium">{pill}</span>
                      </div>
                    ))}
                  </div>
               </Card>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Bishop;